import { ErrorComponentProps, Link } from "@tanstack/react-router";
import { Button } from "./ui/button";
import { logger } from "@/lib/logger";
import { VolumeApiError } from "@/lib/volume-api-errors";
import { AlertCircle } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { KnownGenericError } from "@/lib/errors";
import { MerchantAccessError } from "@/lib/query-retry";
import { useEffect } from "react";
import posthog from "posthog-js";
import { env } from "@/env";
import { useAdminRouteContextRecovery } from "@/hooks/use-admin-route-context-recovery";

const isLiveMerchantPortal =
  env.VITE_VOLUME_URL === "https://api.volumepay.io" &&
  import.meta.env.VITE_IS_PLAYWRIGHT !== "true";
const isLocalhost = window.location.hostname === "localhost";
const isPostHogErrorTrackingEnabled =
  isLiveMerchantPortal &&
  !isLocalhost &&
  env.VITE_PUBLIC_POSTHOG_KEY.trim().length > 0;

export function DefaultRouterErrorComponent({
  error,
  reset,
}: Readonly<{ error: Error; reset: () => void }>) {
  const retry = useAdminRouteContextRecovery(error, reset);
  const isVolumeApiError = error instanceof VolumeApiError;
  const isKnownGenericError = error instanceof KnownGenericError;

  let errorMessage = "Something went wrong with the Volume Merchant Portal";
  if (isVolumeApiError) {
    errorMessage = error.message;
  } else if (isKnownGenericError) {
    errorMessage = error.userFriendlyMessage;
  }

  useEffect(() => {
    if (!isPostHogErrorTrackingEnabled) return;

    posthog.captureException(error, {
      app: "merchant-portal",
      source: "router-error",
      errorName: error.name,
    });
  }, [error]);

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-[var(--radius-surface)] border-slate-200 shadow-lg shadow-slate-200/70">
        <CardHeader className="pb-0">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-destructive" color="red" />
          </div>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <h1 className="text-4xl font-semibold tracking-tight">Oops!</h1>
          <h2 className="text-2xl font-semibold tracking-tight mt-4">
            {errorMessage}
          </h2>
          {error instanceof MerchantAccessError ? (
            <Button asChild>
              <Link to="/admin" search={{}}>
                Back to accounts
              </Link>
            </Button>
          ) : (
            <Button onClick={retry} variant="destructive" className="">
              Try Again?
            </Button>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            If this problem persists,{" "}
            <a
              href={`mailto:support@getvolume.com?subject=[Merchant Portal] ${isVolumeApiError ? error.traceId : ""}`}
              className="font-medium text-primary hover:underline"
            >
              contact support
            </a>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Non retry-able fatal error
 */
export function RootErrorComponent({
  errorProps,
  captureInPostHog = true,
}: Readonly<{
  errorProps?: ErrorComponentProps;
  captureInPostHog?: boolean;
}>) {
  const urlParams = new URLSearchParams(window.location.search);
  const applicationId = urlParams.get("applicationId");

  useEffect(() => {
    if (!captureInPostHog || !isPostHogErrorTrackingEnabled) return;

    posthog.captureException(
      errorProps?.error ?? new Error("Unknown merchant portal root error"),
      {
        app: "merchant-portal",
        source: "root-error",
        applicationId,
        errorName: errorProps?.error.name,
      },
    );
  }, [applicationId, captureInPostHog, errorProps]);

  if (errorProps) {
    logger.error(
      errorProps.error.message,
      {
        ...errorProps.info,
        applicationId,
      },
      errorProps.error,
    );
  } else {
    logger.error("Unknown error", {
      applicationId,
    });
  }

  return (
    <div className="flex justify-center w-screen h-screen">
      <img
        src="/error-gradient.png"
        alt="image description"
        style={{
          objectFit: "cover",
          display: "block",
          width: "100%",
          height: "100%",
        }}
      />
      <div className="absolute flex flex-col items-center rounded-[var(--radius-surface)] border border-slate-200 bg-white py-12 px-16 text-center shadow-lg shadow-slate-200/70 mt-[200px]">
        <h2 className="mb-8">There was an error!</h2>
        <h4 className="mb-8">
          Something went wrong with the Volume Merchant Portal
        </h4>
        <div className="text-[24px] mb-8">
          Please try again or contact support.
        </div>
        <Button asChild variant="outline" className="mb-8">
          <a href="mailto:support@getvolume.com?subject=[Merchant Portal] Fatal Error">
            Email Support
          </a>
        </Button>
        <img src="/volume-logo.svg" alt="Volume Logo" height={60} width={70} />
      </div>
    </div>
  );
}
